import { controlAnswerKey } from "../../utils/regulatoryIdentity"
import { useLayoutEffect, useMemo, useState } from "react"
import { ICloudRegulatoryControlPoint, IEN1176AnswerMap } from "../../types"
import { ControlDefinition, ControlHeading, RecordedFinding } from "./RegulatoryControlCard"
import {
  splitPrintControls,
  PrintableControl,
  packMeasuredCards,
} from "../../utils/regulatoryPrint"
export function PrintControlCard({
  point,
  answers,
}: {
  point: PrintableControl
  answers: IEN1176AnswerMap
}) {
  const answer = point.printAnswer ?? answers[controlAnswerKey(point)]
  return (
    <article className="reg-control-card reg-control-print">
      <ControlHeading point={point} answer={answer} />
      {point.ClassificationReviewed === false && (
        <small>Classement de présentation proposé ou non revu</small>
      )}
      {point.printContinuation && <p>Suite du contrôle</p>}
      <ControlDefinition point={point} print />
      <RecordedFinding answer={answer} />
    </article>
  )
}
export type PrintGroup = {
  key: string
  points: ICloudRegulatoryControlPoint[]
  answers: IEN1176AnswerMap
  firstHeight: number
}
export function useMeasuredControlPages(groups: PrintGroup[]) {
  const key = JSON.stringify(groups)
  const prepared = useMemo(
    () => groups.map((g) => ({ ...g, fragments: splitPrintControls(g.points, g.answers) })),
    [key],
  )
  const [container, setContainer] = useState<HTMLDivElement | null>(null)
  const [layout, setLayout] = useState<{
    key: string
    pages: Record<string, PrintableControl[][]>
  }>({ key: "", pages: {} })
  const [fontVersion, setFontVersion] = useState(0)
  useLayoutEffect(() => {
    let live = true
    document.fonts?.ready.then(() => {
      if (live) setFontVersion((n) => n + 1)
    })
    return () => {
      live = false
    }
  }, [])
  useLayoutEffect(() => {
    if (!container) return
    const pages: Record<string, PrintableControl[][]> = {}
    prepared.forEach((group, index) => {
      const children = container.children[index]!.children
      const heights = Array.from(children).map((el) => el.getBoundingClientRect().height + 14)
      pages[group.key] = packMeasuredCards(group.fragments, heights, group.firstHeight, 700)
    })
    setLayout({ key, pages })
  }, [container, prepared, key, fontVersion])
  const measuring = (
    <div
      ref={setContainer}
      aria-hidden="true"
      className="reg-print-measure"
      style={{
        position: "fixed",
        left: -20000,
        top: 0,
        width: "194mm",
        visibility: "hidden",
        pointerEvents: "none",
      }}
    >
      {prepared.map((g) => (
        <div key={g.key}>
          {g.fragments.map((p, i) => (
            <PrintControlCard key={i} point={p} answers={g.answers} />
          ))}
        </div>
      ))}
    </div>
  )
  return { pages: layout.key === key ? layout.pages : {}, ready: layout.key === key, measuring }
}
